import { JumpTarget } from '@/components/MagicJump';
import type { IProductListItem } from '@/api/types/product';
import { EPlatform } from '@/utils/enums/platform-enum';
import type { StyleHotProductRuntimeData } from './pages/StyleHotProductsPage/types';

/** 活动类型枚举 */
export enum ActivityType {
  GUIDE = 'guide',
  MARKETING = 'marketing',
  PROMOTION = 'promotion',
  SEEDING = 'seeding',
  AUTO_PRODUCT_PUSH = 'auto_product_push',
  STYLE_HOT_PRODUCTS = 'style_hot_products',
}

/** 预览模式密钥 */
export const PREVIEW_KEY = 'C3E36989F19A8678';

/** 有效期结束方式 */
export enum EndMethod {
  REDIRECT = 1,
  NOT_FOUND = 2,
}

// ==================== 原始接口返回结构（与后端对齐）====================

interface RawFaqItem {
  level?: number | string;
  question?: string;
  qustion?: string;
  answer?: string;
}

interface RawFaqData {
  title?: string;
  faq_arr?: RawFaqItem[];
  data_array?: RawFaqItem[];
  faq_list?: RawFaqItem[];
}

/** 接口原始 SEO 结构 */
interface RawSeoData {
  h_one?: string;
  seo_h1?: string;
  seo_title?: string;
  seo_keywords?: string;
  seo_description?: string;
  seo_og_image?: string;
  geo_language_mark?: string;
  faq_list?: RawFaqItem[];
}

/** 接口原始 Banner (ad_banner) 结构 */
interface RawAdBanner {
  alt?: string;
  jump: JumpTarget;
  pc_banner_image?: string;
  wap_banner_image?: string;
}

interface RawMarketingBannerItem {
  uid?: string;
  alt?: string;
  image_alt?: string;
  jump?: JumpTarget;
  jump_content?: JumpTarget;
  pc_banner_image?: string;
  wap_banner_image?: string;
  pc_image?: string;
  wap_image?: string;
  image?: string;
  type?: number | string;
  aspectRatio?: string;
  aspect_ratio?: string;
}

/** 接口原始 blocks 结构 */
interface RawBlocks {
  ad_banner?: RawAdBanner;
  banner_list?: RawMarketingBannerItem[];
  brand_products?: RawMarketingProductBlock;
  coupon_block?: RawMarketingCouponBlock;
  coupon_config?: RawMarketingCouponConfig;
  faq?: RawFaqData;
  faq_part?: RawFaqData;
  flash_sale_block?: RawMarketingFlashSaleBlock;
  guide_data?: RawGuideData;
  marketing_data?: RawMarketingData;
  nav_product?: RawMarketingData['nav_product'];
  nav_product_sections?: RawMarketingNavProductSection[];
  page_style?: RawMarketingPageStyle;
  promotion_data?: RawPromotionData;
  product_recommend?: {
    group_id?: number | null;
    source_type?: string;
    section_title?: string;
    algorithm_value?: string;
  };
  ranking_block?: RawMarketingRankingBlock;
  scene_products?: RawMarketingSceneProductsBlock;
  schema_version?: number | string;
  [key: string]: unknown;
}

export interface MarketingRuntimeCoupon {
  id: number | string;
  model_type?: number | string;
  name?: string;
  way?: number | string;
  code_status?: number;
  is_get?: number;
  status?: number | string;
  count_down?: number;
  twenty_four_count_time?: number;
  time_interval?: string[];
  discounts_details?: unknown;
  full_discounts_details?: unknown;
  appoint_discounts_details?: unknown;
  free_discounts_details?: unknown;
  extension_field?: {
    coupon_label?: {
      tag_text?: string;
    };
  };
  [key: string]: unknown;
}

export interface MarketingCouponConfig {
  config_type?: 'rule_set' | 'manual_coupon_id' | string;
  rule_type?: string;
  display_count?: number;
  coupon_ids?: Array<number | string>;
  coupons: MarketingRuntimeCoupon[];
  visible: boolean;
}

export interface MarketingCouponsResponse {
  enabled: boolean;
  coupon_ids: Array<number | string>;
  coupon_list: MarketingRuntimeCoupon[];
}

export interface MarketingCouponReceiveFailedReason {
  coupon_id: number | string;
  message: string;
}

export interface MarketingCouponsReceiveResponse {
  success_count: number;
  failed_count: number;
  success_coupon_ids: Array<number | string>;
  failed_coupon_ids: Array<number | string>;
  failed_reasons: MarketingCouponReceiveFailedReason[];
}

interface RawMarketingCouponConfig {
  config_type?: string;
  rule_type?: string;
  display_count?: number;
  coupon_ids?: Array<number | string>;
  coupons?: MarketingRuntimeCoupon[];
  coupon_list?: MarketingRuntimeCoupon[];
  visible?: boolean;
}

interface RawMarketingCouponBlock {
  coupon_list?: MarketingRuntimeCoupon[];
  enabled?: boolean;
  visible?: boolean;
  title?: string;
  title_text?: string;
  button_text?: string;
  cta_text?: string;
  background_color?: string;
  bg_color?: string;
  texture_color?: string;
  display_count?: number | string;
  sort?: number | string;
}

interface RawMarketingProductBlock {
  alt?: string;
  enabled?: boolean;
  visible?: boolean;
  title?: string;
  title_text?: string;
  sub_title?: string;
  subtitle?: string;
  description?: string;
  button_text?: string;
  cta_text?: string;
  button_link?: string;
  jump_url?: string;
  background_color?: string;
  bg_color?: string;
  display_count?: number | string;
  sort?: number | string;
  product_group_id?: number | string;
  hover_image_rule?: unknown[];
  products?: Array<Record<string, unknown>>;
  product_template?: MarketingProductTemplate;
}

interface RawMarketingSceneProductsItem extends RawMarketingProductBlock {
  item_index?: number | string;
  uid?: string;
  alt?: string;
  name?: string;
  nav_title?: string;
  scene_title?: string;
  style_title?: string;
  wap_tab_image?: string;
  pc_tab_image?: string;
  pc_image?: string;
  wap_image?: string;
  image?: string;
  nav_pc_image?: string;
  nav_wap_image?: string;
  product_count?: number | string;
  product_ids?: Array<number | string>;
  sort?: number | string;
}

interface RawMarketingSceneProductsBlock extends RawMarketingProductBlock {
  pc_background_color?: string;
  wap_background_color?: string;
  scenes?: RawMarketingSceneProductsItem[];
  scene_list?: RawMarketingSceneProductsItem[];
  scene_product_list?: RawMarketingSceneProductsItem[];
  category_list?: RawMarketingSceneProductsItem[];
  style_list?: RawMarketingSceneProductsItem[];
  my_product_list?: RawMarketingSceneProductsItem[];
}

interface RawMarketingRankingItem extends RawMarketingProductBlock {
  item_index?: number | string;
  uid?: string;
  name?: string;
  tab_name?: string;
  tab_title?: string;
  ranking_title?: string;
  rank_title?: string;
  show_rating?: boolean;
  sort?: number | string;
}

interface RawMarketingRankingBlock extends RawMarketingProductBlock {
  wap_background_color?: string;
  rankings?: RawMarketingRankingItem[];
  ranking_list?: RawMarketingRankingItem[];
  rank_list?: RawMarketingRankingItem[];
  list?: RawMarketingRankingItem[];
  tabs?: RawMarketingRankingItem[];
  my_product_list?: RawMarketingRankingItem[];
}

type RawMarketingFlashSaleReachLimitRule = 'hide_immediately' | 'show_today' | 'show_until_end';

interface RawMarketingFlashSaleStyleConfig {
  title?: string;
  sub_title?: string;
  subtitle?: string;
  description?: string;
  bg_wap_image?: string;
  bg_pc_image?: string;
  background_color?: string;
  bg_color?: string;
  button_text?: string;
  cta_text?: string;
  button_wap_image?: string;
  button_pc_image?: string;
  button_color?: string;
  flash_sale_id?: number | string;
  product_source?: number | string;
}

interface RawMarketingFlashSaleBlock {
  enabled?: boolean;
  visible?: boolean;
  reach_limit_rule?: RawMarketingFlashSaleReachLimitRule;
  new_customer_style?: RawMarketingFlashSaleStyleConfig;
  old_customer_style?: RawMarketingFlashSaleStyleConfig;
  reached_limit_style?: RawMarketingFlashSaleStyleConfig;
  flash_sale_id?: number | string;
  title?: string;
  title_text?: string;
  sub_title?: string;
  subtitle?: string;
  description?: string;
  time_text?: string;
  countdown_text?: string;
  button_text?: string;
  cta_text?: string;
  background_color?: string;
  bg_color?: string;
  bg_pc_image?: string;
  bg_wap_image?: string;
  button_pc_image?: string;
  button_wap_image?: string;
  button_color?: string;
  countdown_end_at?: string | null;
  end_at?: string | null;
  remaining_seconds?: number | string | null;
  count_down?: number | string | null;
  countdown_seconds?: number | string | null;
  display_count?: number | string;
  sort?: number | string;
  sold_out_sort?: number | string;
  sold_out_display?: number | string;
  hover_image_rule?: unknown[];
  products?: Array<Record<string, unknown>>;
  product_template?: MarketingProductTemplate;
}

export interface MarketingProductTemplate {
  type?: 'product_group' | 'rule_recommend' | string;
  rule_type?: string;
  product_group_id?: number | string;
  limit?: number;
  products: Array<Record<string, unknown>>;
  page?: number;
  page_size?: number;
  total?: number;
  has_more?: boolean;
  visible?: boolean;
}

interface RawMarketingNavProductSection {
  alt?: string;
  nav_title?: string;
  nav_pc_image?: string;
  nav_wap_image?: string;
  product_group_id?: string | number;
  product_pc_image?: string;
  product_wap_image?: string;
  relation_type?: string;
  rule_type?: string;
  sort?: number;
  product_template?: MarketingProductTemplate;
}

interface RawGuideData {
  pc_nav_list?: RawGuidePrimaryNavItem[];
  wap_nav_list?: RawGuidePrimaryNavItem[];
}

interface RawGuidePrimaryNavItem {
  image?: string | null;
  title?: string;
  children?: RawGuideSecondaryNavItem[];
}

interface RawGuideSecondaryNavItem {
  image?: string | null;
  title?: string;
  is_show?: boolean;
  banner_image?: string | null;
  product_group_id?: string | number;
}

/** 营销主活动页原始数据 */
interface RawMarketingPageStyle {
  background_color?: string;
  pc_background_color?: string;
  wap_background_color?: string;
  background_color_pc?: string;
  background_color_wap?: string;
  pc_bg_color?: string;
  wap_bg_color?: string;
}

interface RawMarketingData {
  schema_version?: number | string;
  page_style?: RawMarketingPageStyle;
  banner_list?: RawMarketingBannerItem[];
  coupon_block?: RawMarketingCouponBlock;
  flash_sale_block?: RawMarketingFlashSaleBlock;
  brand_products?: RawMarketingProductBlock;
  scene_products?: RawMarketingSceneProductsBlock;
  ranking_block?: RawMarketingRankingBlock;
  nav_product?: {
    enabled?: boolean;
    nav_product_list?: RawMarketingNavProductItem[];
    my_product_list?: RawMarketingNavProductItem[];
  } & Record<string, unknown>;
  nav_product_list?: RawMarketingNavProductItem[];
}

interface RawMarketingNavProductItem {
  alt?: string;
  nav_title?: string;
  nav_pc_image?: string;
  nav_wap_image?: string;
  product_group_id?: string | number;
  product_pc_image?: string;
  product_wap_image?: string;
  relation_type?: string;
  rule_type?: string;
  sort?: number;
  product_template?: MarketingProductTemplate;
}

interface RawPromotionData {
  fixed_ads?: RawPromotionFixedAd[];
  flash_sale?: RawPromotionFlashSale;
  promotion_position?: RawPromotionPosition;
}

interface RawPromotionFixedAd {
  alt?: string;
  jump?: JumpTarget;
  pc_image?: string;
  wap_image?: string;
}

interface RawPromotionCustomerFlashSale {
  title?: string;
  sub_title?: string;
  bg_pc_image?: string;
  bg_wap_image?: string;
  button_text?: string;
  button_color?: string;
  product_source?: string | number;
  button_pc_image?: string;
  button_wap_image?: string;
  background_color?: string;
}

interface RawPromotionFlashSale {
  new_customer?: RawPromotionCustomerFlashSale;
  old_customer?: RawPromotionCustomerFlashSale;
  sold_out_sort?: string | number;
  sold_out_display?: string | number;
}

interface RawPromotionPosition {
  alt?: string;
  title_pc_image?: string;
  title_wap_image?: string;
  product_group_id?: string | number;
}

/** 接口原始 page_meta 结构 */
interface RawPageMeta {
  end_at?: string;
  end_jump?: JumpTarget;
  end_method?: EndMethod;
  end_redirect?: null;
  validity_type?: number;
}

/** 模板类型枚举值 */
export type MarketingPageTemplateType =
  | 'marketing'
  | 'auto_product_push'
  | 'promotion'
  | 'seeding'
  | 'guide'
  | 'style_hot_products'
  | 'trend'
  | 'custom';

export function isMarketingPageActivityType(activityType?: MarketingPageTemplateType | null) {
  return activityType === ActivityType.MARKETING || activityType === ActivityType.AUTO_PRODUCT_PUSH;
}

/** 接口 data 字段的原始形状 */
export interface RawMarketingRuntimeConfigData {
  /** 落地页主键 */
  marketing_page_id?: number;
  /** 模板类型 */
  template_type?: MarketingPageTemplateType;
  /** 模板类型前台展示名（英文） */
  page_type_name?: string;
  /** 归属营销活动 id；未绑定时为 null */
  activity_id?: number | null;
  /** 归属营销活动名称 */
  activity_name?: string;
  /** 页面标题（前台展示） */
  page_title?: string;
  /** 页面名称（埋点 page_name） */
  page_name?: string;
  /** 页面状态：1=草稿 2=已发布 3=已结束 */
  page_status?: number;
  page_meta?: RawPageMeta;
  seo?: RawSeoData;
  blocks?: RawBlocks;
  faq?: RawFaqData;
  faq_part?: RawFaqData;
  [key: string]: unknown;
}

// ==================== 规范化后的内部类型 ====================

/** SEO 数据 */
export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  hOne?: string;
  seoH1?: string;
  geoLanguageMark?: string;
  width?: string;
  height?: string;
}

/** Banner 数据 */
export interface BannerData {
  image: string;
  image_alt?: string;
  type?: number; // 1=视频, 2=图片
  aspectRatio?: string;
  jump_content?: JumpTarget;
}

/** Runtime Config 接口响应（规范化后的业务类型） */
export interface MarketingRuntimeConfig {
  activity_id?: number;
  activity_name?: string;
  marketing_page_id?: number;
  slug: string;
  /** 模板类型 */
  template_type?: MarketingPageTemplateType;
  /** 页面标题（浏览器标签页） */
  page_title?: string;
  page_name?: string;
  /** 页面状态：1=草稿 2=已发布 3=已结束 */
  page_status?: number;
  validity_type?: number; // 1=需要校验有效期
  start_at?: string;
  end_at?: string;
  end_method?: EndMethod;
  end_jump?: JumpTarget;
  seo?: SeoData;
  ad_banner?: BannerData;
  wap_ad_banner?: BannerData;
  faq?: FaqData;
  /** 商品推荐版位配置 */
  product_recommend?: ProductRecommendConfig;
  /** 自动化推品运行时优惠券配置 */
  coupon_config?: MarketingCouponConfig;
  /** 导购活动页配置 */
  guide_data?: GuideData;
  /** 营销主活动页配置 */
  marketing_data?: MarketingMainData;
  /** 促销活动页配置 */
  promotion_data?: PromotionData;
}

export interface GuideData {
  pc_nav_list: GuidePrimaryNavItem[];
  wap_nav_list: GuidePrimaryNavItem[];
}

export interface GuidePrimaryNavItem {
  image?: string | null;
  title?: string;
  children: GuideSecondaryNavItem[];
}

export interface GuideSecondaryNavItem {
  image?: string | null;
  title?: string;
  is_show?: boolean;
  banner_image?: string | null;
  product_group_id?: string | number;
}

/** 营销主活动页配置 */
export interface MarketingMainPageStyle {
  pc_background_color: string;
  wap_background_color: string;
}

export interface MarketingMainData {
  schema_version?: number | string;
  page_style: MarketingMainPageStyle;
  modules: MarketingMainModuleConfig[];
  banner_list: BannerData[];
  coupon_block?: MarketingCouponBlockConfig;
  flash_sale_block?: MarketingFlashSaleBlockConfig;
  brand_products?: MarketingProductBlockConfig;
  scene_products?: MarketingSceneProductsBlockConfig;
  ranking_block?: MarketingRankingBlockConfig;
  nav_product_list: MarketingNavProductItem[];
}

export enum MarketingMainModuleType {
  BANNER = 'banner',
  COUPON = 'coupon',
  FLASH_SALE = 'flash_sale',
  BRAND_PRODUCTS = 'brand_products',
  SCENE_PRODUCTS = 'scene_products',
  RANKING = 'ranking',
  NAVIGATION_PRODUCTS = 'navigation_products',
}

export interface MarketingMainModuleConfig {
  id: string;
  type: MarketingMainModuleType;
  sort: number;
}

export interface MarketingCouponBlockConfig {
  visible: boolean;
  title: string;
  button_text: string;
  background_color: string;
  texture_color?: string;
  display_count: number;
  sort: number;
}

export type MarketingFlashSaleReachLimitRule = 'hide_immediately' | 'show_today' | 'show_until_end';

export type MarketingFlashSaleStatus = 'normal' | 'after_limit' | 'hidden';

export type MarketingFlashSaleVisibleState =
  | 'new_customer'
  | 'old_customer'
  | 'after_limit'
  | 'hidden';

export type MarketingFlashSaleCustomerSegment = 'new_customer' | 'old_customer';

export interface MarketingFlashSaleStyleConfig {
  title: string;
  sub_title?: string;
  bg_wap_image: string;
  bg_pc_image?: string;
  background_color?: string;
  button_text?: string;
  button_wap_image?: string;
  button_pc_image?: string;
  button_color?: string;
  flash_sale_id?: number | string;
}

export interface MarketingFlashSaleBlockConfig {
  visible: boolean;
  status?: MarketingFlashSaleStatus;
  visible_state?: MarketingFlashSaleVisibleState;
  reach_limit_rule?: MarketingFlashSaleReachLimitRule;
  new_customer_style?: MarketingFlashSaleStyleConfig;
  old_customer_style?: MarketingFlashSaleStyleConfig;
  reached_limit_style?: MarketingFlashSaleStyleConfig;
  flash_sale_id?: number | string;
  new_customer_flash_sale_id?: number | string;
  old_customer_flash_sale_id?: number | string;
  customer_segment?: MarketingFlashSaleCustomerSegment;
  participated?: boolean;
  last_paid_date?: string | null;
  last_paid_participation_date?: string | null;
  is_reached_limit?: boolean;
  is_targeted?: boolean;
  title: string;
  sub_title: string;
  time_text: string;
  button_text: string;
  background_color: string;
  bg_pc_image?: string;
  bg_wap_image?: string;
  button_pc_image?: string;
  button_wap_image?: string;
  button_color: string;
  countdown_end_at?: string | null;
  remaining_seconds?: number | null;
  display_count: number;
  sort: number;
  sold_out_sort?: number | string;
  sold_out_display?: number | string;
  hover_image_rule?: unknown[];
  product_template?: MarketingProductTemplate;
  products: Array<Record<string, unknown>>;
}

export interface MarketingProductBlockConfig {
  visible: boolean;
  alt?: string;
  title: string;
  sub_title: string;
  button_text: string;
  button_link?: string;
  background_color?: string;
  display_count: number;
  sort: number;
  product_group_id?: number | string;
  hover_image_rule?: unknown[];
  product_template?: MarketingProductTemplate;
  products: Array<Record<string, unknown>>;
}

export interface MarketingSceneProductsItem extends MarketingProductBlockConfig {
  item_index?: number;
  products_loaded?: boolean;
  uid?: string;
  alt?: string;
  title: string;
  pc_image?: string;
  wap_image?: string;
  image?: string;
  product_count?: number;
  product_ids?: Array<number | string>;
}

export interface MarketingSceneProductsBlockConfig extends MarketingProductBlockConfig {
  pc_background_color?: string;
  wap_background_color?: string;
  default_scene_index?: number;
  scenes: MarketingSceneProductsItem[];
}

export interface MarketingRankingItem extends MarketingProductBlockConfig {
  item_index?: number;
  uid?: string;
  tab_title: string;
  show_rating?: boolean;
}

export interface MarketingRankingBlockConfig extends MarketingProductBlockConfig {
  wap_background_color?: string;
  rankings: MarketingRankingItem[];
}

export interface MarketingNavProductItem {
  alt?: string;
  nav_title?: string;
  nav_pc_image?: string;
  nav_wap_image?: string;
  product_group_id?: string | number;
  product_pc_image?: string;
  product_wap_image?: string;
  relation_type?: string;
  rule_type?: string;
  sort?: number;
  product_template?: MarketingProductTemplate;
}

export interface PromotionData {
  fixed_ads: PromotionFixedAd[];
  flash_sale?: PromotionFlashSale;
  promotion_position?: PromotionPosition;
}

export interface PromotionFixedAd {
  alt?: string;
  jump?: JumpTarget;
  pc_image?: string;
  wap_image?: string;
}

export interface PromotionCustomerFlashSale {
  title?: string;
  sub_title?: string;
  bg_pc_image?: string;
  bg_wap_image?: string;
  button_text?: string;
  button_color?: string;
  product_source?: string | number;
  button_pc_image?: string;
  button_wap_image?: string;
  background_color?: string;
}

export type PromotionFlashSaleCustomerSegment = 'new_user' | 'old_user';

export interface PromotionFlashSale {
  new_customer?: PromotionCustomerFlashSale;
  old_customer?: PromotionCustomerFlashSale;
  sold_out_sort?: string | number;
  sold_out_display?: string | number;
}

export interface PromotionPosition {
  alt?: string;
  title_pc_image?: string;
  title_wap_image?: string;
  product_group_id?: string | number;
}

/** 商品推荐版位配置 */
export interface ProductRecommendConfig {
  /** 分组ID (source_type='group' 时使用) */
  group_id?: number | null;
  /** 数据来源：group=指定分组, algorithm=算法推荐 */
  source_type: 'group' | 'algorithm' | '';
  /** 版块标题 */
  section_title?: string;
  /** 算法值 (source_type='algorithm' 时使用) */
  algorithm_value?: string;
}

/** FAQ 数据 */
export interface FaqData {
  title: string;
  faq_arr?: FaqItem[];
  data_array?: FaqItem[];
}

export interface FaqItem {
  question?: string;
  qustion?: string; // 后端字段名有误，需兼容
  answer?: string;
  level?: number;
}

const LEGACY_MARKETING_MAIN_BACKGROUND_COLOR = '#FFFFFF';
const DEFAULT_MARKETING_MAIN_CONFIG_BACKGROUND_COLOR = '#F4F4F4';

function normalizeOptionalString(value?: string): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function getGeoLanguageMarkValues(value?: string): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeFaqLevel(level?: number | string): number {
  const parsed = Number(level);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 6 ? parsed : 2;
}

function normalizeFaqData(faqData?: RawFaqData, fallbackTitle = 'FAQ'): FaqData | undefined {
  if (!faqData) {
    return undefined;
  }

  const rawItems = Array.isArray(faqData.data_array)
    ? faqData.data_array
    : Array.isArray(faqData.faq_arr)
      ? faqData.faq_arr
      : Array.isArray(faqData.faq_list)
        ? faqData.faq_list
        : [];

  const dataArray = rawItems
    .map((item) => {
      const question = item.question || item.qustion || '';
      return {
        question,
        ...(item.qustion ? { qustion: item.qustion } : {}),
        answer: item.answer || '',
        level: normalizeFaqLevel(item.level),
      };
    })
    .filter((item) => item.question || item.answer);

  if (dataArray.length === 0) {
    return undefined;
  }

  return {
    title: faqData.title || fallbackTitle,
    data_array: dataArray,
  };
}

function normalizeRuntimeFaqData(
  raw: RawMarketingRuntimeConfigData,
  blocks: RawBlocks,
  seo: RawSeoData,
): FaqData | undefined {
  const fallbackTitle = seo.seo_h1 || seo.h_one || raw.page_name || raw.page_title || 'FAQ';

  return (
    normalizeFaqData(raw.faq, fallbackTitle) ||
    normalizeFaqData(blocks.faq, fallbackTitle) ||
    normalizeFaqData(raw.faq_part, fallbackTitle) ||
    normalizeFaqData(blocks.faq_part, fallbackTitle) ||
    normalizeFaqData(
      Array.isArray(seo.faq_list) ? { title: seo.seo_h1, data_array: seo.faq_list } : undefined,
      fallbackTitle,
    )
  );
}

function normalizeMarketingCouponConfig(
  couponConfig?: RawMarketingCouponConfig | RawMarketingCouponBlock,
): MarketingCouponConfig | undefined {
  if (!couponConfig) {
    return undefined;
  }

  const coupons =
    'coupons' in couponConfig && Array.isArray(couponConfig.coupons)
      ? couponConfig.coupons
      : Array.isArray(couponConfig.coupon_list)
        ? couponConfig.coupon_list
        : [];
  const displayCount = Number(couponConfig.display_count);

  return {
    config_type: 'config_type' in couponConfig ? couponConfig.config_type : undefined,
    rule_type: 'rule_type' in couponConfig ? couponConfig.rule_type : undefined,
    display_count: Number.isFinite(displayCount) && displayCount > 0 ? displayCount : undefined,
    coupon_ids:
      'coupon_ids' in couponConfig && Array.isArray(couponConfig.coupon_ids)
        ? couponConfig.coupon_ids
        : [],
    coupons,
    visible:
      ('enabled' in couponConfig ? couponConfig.enabled !== false : true) &&
      (couponConfig.visible === true || coupons.length > 0),
  };
}

function normalizeMarketingCouponBlock(
  couponBlock?: RawMarketingCouponBlock,
): MarketingCouponBlockConfig | undefined {
  if (!couponBlock) {
    return undefined;
  }

  const displayCount = Number(couponBlock.display_count);
  const sort = Number(couponBlock.sort);
  const textureColor = normalizeOptionalString(couponBlock.texture_color);

  return {
    visible: couponBlock.enabled !== false && couponBlock.visible !== false,
    title:
      normalizeOptionalString(couponBlock.title) || normalizeOptionalString(couponBlock.title_text),
    button_text:
      normalizeOptionalString(couponBlock.button_text) ||
      normalizeOptionalString(couponBlock.cta_text),
    background_color:
      normalizeOptionalString(couponBlock.background_color) ||
      normalizeOptionalString(couponBlock.bg_color) ||
      '#D9D1FF',
    ...(textureColor ? { texture_color: textureColor } : {}),
    display_count: Number.isFinite(displayCount) && displayCount > 0 ? displayCount : 5,
    sort: Number.isFinite(sort) ? sort : 20,
  };
}

function normalizeMarketingProductTemplate(
  productTemplate?: MarketingProductTemplate,
): MarketingProductTemplate | undefined {
  if (!productTemplate) {
    return undefined;
  }

  return {
    ...productTemplate,
    products: Array.isArray(productTemplate.products) ? productTemplate.products : [],
    visible: productTemplate.visible === true,
  };
}

function normalizeMarketingFlashSaleStyle(
  style?: RawMarketingFlashSaleStyleConfig,
): MarketingFlashSaleStyleConfig | undefined {
  if (!style) {
    return undefined;
  }

  const flashSaleId = style.flash_sale_id ?? style.product_source;
  const bgWapImage = normalizeOptionalString(style.bg_wap_image);
  const normalizedStyle: MarketingFlashSaleStyleConfig = {
    title: normalizeOptionalString(style.title),
    bg_wap_image: bgWapImage,
  };
  const subTitle =
    normalizeOptionalString(style.sub_title) ||
    normalizeOptionalString(style.subtitle) ||
    normalizeOptionalString(style.description);
  const bgPcImage = normalizeOptionalString(style.bg_pc_image);
  const backgroundColor =
    normalizeOptionalString(style.background_color) || normalizeOptionalString(style.bg_color);
  const buttonText =
    normalizeOptionalString(style.button_text) || normalizeOptionalString(style.cta_text);
  const buttonWapImage = normalizeOptionalString(style.button_wap_image);
  const buttonPcImage = normalizeOptionalString(style.button_pc_image);
  const buttonColor = normalizeOptionalString(style.button_color);

  return {
    ...normalizedStyle,
    ...(subTitle ? { sub_title: subTitle } : {}),
    ...(bgPcImage ? { bg_pc_image: bgPcImage } : {}),
    ...(backgroundColor ? { background_color: backgroundColor } : {}),
    ...(buttonText ? { button_text: buttonText } : {}),
    ...(buttonWapImage ? { button_wap_image: buttonWapImage } : {}),
    ...(buttonPcImage ? { button_pc_image: buttonPcImage } : {}),
    ...(buttonColor ? { button_color: buttonColor } : {}),
    ...(flashSaleId ? { flash_sale_id: flashSaleId } : {}),
  };
}

const UNLIMITED_MARKETING_FLASH_SALE_DISPLAY_COUNT = Number.MAX_SAFE_INTEGER;

function normalizeMarketingFlashSaleBlock(
  flashSaleBlock?: RawMarketingFlashSaleBlock,
): MarketingFlashSaleBlockConfig | undefined {
  if (!flashSaleBlock) {
    return undefined;
  }

  const displayCount = Number(flashSaleBlock.display_count);
  const sort = Number(flashSaleBlock.sort);
  const remainingSeconds = Number(
    flashSaleBlock.remaining_seconds ??
      flashSaleBlock.count_down ??
      flashSaleBlock.countdown_seconds,
  );
  const productTemplate = normalizeMarketingProductTemplate(flashSaleBlock.product_template);
  const newCustomerStyle = normalizeMarketingFlashSaleStyle(flashSaleBlock.new_customer_style);
  const oldCustomerStyle = normalizeMarketingFlashSaleStyle(flashSaleBlock.old_customer_style);
  const reachedLimitStyle = normalizeMarketingFlashSaleStyle(flashSaleBlock.reached_limit_style);
  const defaultStyle = newCustomerStyle || oldCustomerStyle || reachedLimitStyle;
  const products = Array.isArray(flashSaleBlock.products)
    ? flashSaleBlock.products
    : flashSaleBlock.product_template?.visible === false
      ? []
      : productTemplate?.products || [];

  return {
    visible: flashSaleBlock.enabled !== false && flashSaleBlock.visible !== false,
    reach_limit_rule: flashSaleBlock.reach_limit_rule,
    new_customer_style: newCustomerStyle,
    old_customer_style: oldCustomerStyle,
    reached_limit_style: reachedLimitStyle,
    flash_sale_id:
      flashSaleBlock.flash_sale_id ||
      defaultStyle?.flash_sale_id ||
      newCustomerStyle?.flash_sale_id ||
      oldCustomerStyle?.flash_sale_id,
    new_customer_flash_sale_id: newCustomerStyle?.flash_sale_id,
    old_customer_flash_sale_id: oldCustomerStyle?.flash_sale_id,
    title:
      normalizeOptionalString(defaultStyle?.title) ||
      normalizeOptionalString(flashSaleBlock.title) ||
      normalizeOptionalString(flashSaleBlock.title_text),
    sub_title:
      normalizeOptionalString(defaultStyle?.sub_title) ||
      normalizeOptionalString(flashSaleBlock.sub_title) ||
      normalizeOptionalString(flashSaleBlock.subtitle) ||
      normalizeOptionalString(flashSaleBlock.description),
    time_text:
      normalizeOptionalString(flashSaleBlock.time_text) ||
      normalizeOptionalString(flashSaleBlock.countdown_text),
    button_text:
      normalizeOptionalString(defaultStyle?.button_text) ||
      normalizeOptionalString(flashSaleBlock.button_text) ||
      normalizeOptionalString(flashSaleBlock.cta_text),
    background_color:
      normalizeOptionalString(defaultStyle?.background_color) ||
      normalizeOptionalString(flashSaleBlock.background_color) ||
      normalizeOptionalString(flashSaleBlock.bg_color) ||
      '#D9D1FF',
    bg_pc_image:
      normalizeOptionalString(defaultStyle?.bg_pc_image) ||
      normalizeOptionalString(flashSaleBlock.bg_pc_image),
    bg_wap_image:
      normalizeOptionalString(defaultStyle?.bg_wap_image) ||
      normalizeOptionalString(flashSaleBlock.bg_wap_image),
    button_pc_image:
      normalizeOptionalString(defaultStyle?.button_pc_image) ||
      normalizeOptionalString(flashSaleBlock.button_pc_image),
    button_wap_image:
      normalizeOptionalString(defaultStyle?.button_wap_image) ||
      normalizeOptionalString(flashSaleBlock.button_wap_image),
    button_color:
      normalizeOptionalString(defaultStyle?.button_color) ||
      normalizeOptionalString(flashSaleBlock.button_color) ||
      '#463AE8',
    countdown_end_at: flashSaleBlock.countdown_end_at ?? flashSaleBlock.end_at ?? null,
    remaining_seconds:
      Number.isFinite(remainingSeconds) && remainingSeconds > 0 ? remainingSeconds : null,
    display_count:
      Number.isFinite(displayCount) && displayCount > 0
        ? displayCount
        : UNLIMITED_MARKETING_FLASH_SALE_DISPLAY_COUNT,
    sort: Number.isFinite(sort) ? sort : 25,
    sold_out_sort: flashSaleBlock.sold_out_sort,
    sold_out_display: flashSaleBlock.sold_out_display,
    hover_image_rule: Array.isArray(flashSaleBlock.hover_image_rule)
      ? flashSaleBlock.hover_image_rule
      : undefined,
    product_template: productTemplate,
    products,
  };
}

function normalizeMarketingProductBlock(
  productBlock: RawMarketingProductBlock | undefined,
  {
    defaultTitle,
    defaultSubTitle,
    defaultSort,
    defaultDisplayCount,
  }: {
    defaultTitle: string;
    defaultSubTitle: string;
    defaultSort: number;
    defaultDisplayCount: number;
  },
): MarketingProductBlockConfig | undefined {
  if (!productBlock) {
    return undefined;
  }

  const displayCount = Number(productBlock.display_count);
  const sort = Number(productBlock.sort);
  const productTemplate = normalizeMarketingProductTemplate(productBlock.product_template);
  const products = Array.isArray(productBlock.products)
    ? productBlock.products
    : productBlock.product_template?.visible === false
      ? []
      : productTemplate?.products || [];

  return {
    visible: productBlock.enabled !== false && productBlock.visible !== false,
    ...(normalizeOptionalString(productBlock.alt)
      ? { alt: normalizeOptionalString(productBlock.alt) }
      : {}),
    title:
      normalizeOptionalString(productBlock.title) ||
      normalizeOptionalString(productBlock.title_text) ||
      defaultTitle,
    sub_title:
      normalizeOptionalString(productBlock.sub_title) ||
      normalizeOptionalString(productBlock.subtitle) ||
      normalizeOptionalString(productBlock.description) ||
      defaultSubTitle,
    button_text:
      normalizeOptionalString(productBlock.button_text) ||
      normalizeOptionalString(productBlock.cta_text),
    button_link:
      normalizeOptionalString(productBlock.button_link) ||
      normalizeOptionalString(productBlock.jump_url) ||
      undefined,
    background_color:
      normalizeOptionalString(productBlock.background_color) ||
      normalizeOptionalString(productBlock.bg_color) ||
      undefined,
    display_count:
      Number.isFinite(displayCount) && displayCount > 0 ? displayCount : defaultDisplayCount,
    sort: Number.isFinite(sort) ? sort : defaultSort,
    product_group_id: productBlock.product_group_id || productTemplate?.product_group_id,
    hover_image_rule: Array.isArray(productBlock.hover_image_rule)
      ? productBlock.hover_image_rule
      : undefined,
    product_template: productTemplate,
    products,
  };
}

function normalizeMarketingSceneProductsList(
  sceneBlock?: RawMarketingSceneProductsBlock,
): RawMarketingSceneProductsItem[] {
  if (!sceneBlock) {
    return [];
  }

  const rawList =
    sceneBlock.scenes ||
    sceneBlock.scene_list ||
    sceneBlock.scene_product_list ||
    sceneBlock.category_list ||
    sceneBlock.style_list ||
    sceneBlock.my_product_list ||
    [];

  return Array.isArray(rawList)
    ? rawList
        .map((item, index) => ({
          ...item,
          item_index: item.item_index ?? index,
        }))
        .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
    : [];
}

function normalizeMarketingSceneProductsBlock(
  sceneBlock?: RawMarketingSceneProductsBlock,
): MarketingSceneProductsBlockConfig | undefined {
  const baseBlock = normalizeMarketingProductBlock(sceneBlock, {
    defaultTitle: '',
    defaultSubTitle: '',
    defaultSort: 28,
    defaultDisplayCount: 5,
  });

  if (!baseBlock) {
    return undefined;
  }

  const scenes = normalizeMarketingSceneProductsList(sceneBlock).map((item, index) => {
    const productCount = Number(item.product_count);
    const normalizedItem = normalizeMarketingProductBlock(item, {
      defaultTitle:
        normalizeOptionalString(item.scene_title) ||
        normalizeOptionalString(item.style_title) ||
        normalizeOptionalString(item.nav_title) ||
        normalizeOptionalString(item.name),
      defaultSubTitle: '',
      defaultSort: Number(item.sort || index),
      defaultDisplayCount: baseBlock.display_count,
    });

    return {
      ...(normalizedItem || baseBlock),
      item_index: Number.isFinite(Number(item.item_index)) ? Number(item.item_index) : index,
      title:
        normalizeOptionalString(item.scene_title) ||
        normalizeOptionalString(item.style_title) ||
        normalizeOptionalString(item.nav_title) ||
        normalizeOptionalString(item.name) ||
        normalizedItem?.title ||
        '',
      alt: normalizeOptionalString(item.alt),
      uid: normalizeOptionalString(item.uid) || undefined,
      pc_image:
        normalizeOptionalString(item.pc_tab_image) ||
        normalizeOptionalString(item.pc_image) ||
        normalizeOptionalString(item.nav_pc_image) ||
        normalizeOptionalString(item.image),
      wap_image:
        normalizeOptionalString(item.wap_tab_image) ||
        normalizeOptionalString(item.wap_image) ||
        normalizeOptionalString(item.nav_wap_image) ||
        normalizeOptionalString(item.image),
      image: normalizeOptionalString(item.image),
      product_count: Number.isFinite(productCount) ? productCount : undefined,
      product_ids: Array.isArray(item.product_ids) ? item.product_ids : undefined,
    };
  });
  const pcBackgroundColor =
    normalizeOptionalString(sceneBlock?.pc_background_color) ||
    normalizeOptionalString(sceneBlock?.background_color) ||
    normalizeOptionalString(sceneBlock?.bg_color);
  const wapBackgroundColor =
    normalizeOptionalString(sceneBlock?.wap_background_color) ||
    normalizeOptionalString(sceneBlock?.background_color) ||
    normalizeOptionalString(sceneBlock?.bg_color);

  return {
    ...baseBlock,
    ...(pcBackgroundColor ? { pc_background_color: pcBackgroundColor } : {}),
    ...(wapBackgroundColor ? { wap_background_color: wapBackgroundColor } : {}),
    scenes,
  };
}

function normalizeMarketingRankingList(
  rankingBlock?: RawMarketingRankingBlock,
): RawMarketingRankingItem[] {
  if (!rankingBlock) {
    return [];
  }

  const rawList =
    rankingBlock.rankings ||
    rankingBlock.ranking_list ||
    rankingBlock.rank_list ||
    rankingBlock.list ||
    rankingBlock.tabs ||
    rankingBlock.my_product_list ||
    [];

  return Array.isArray(rawList)
    ? rawList
        .map((item, index) => ({
          ...item,
          item_index: item.item_index ?? index,
        }))
        .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
    : [];
}

function normalizeMarketingRankingBlock(
  rankingBlock?: RawMarketingRankingBlock,
): MarketingRankingBlockConfig | undefined {
  const baseBlock = normalizeMarketingProductBlock(rankingBlock, {
    defaultTitle: '',
    defaultSubTitle: '',
    defaultSort: 29,
    defaultDisplayCount: 8,
  });

  if (!baseBlock) {
    return undefined;
  }

  const rawRankingList = normalizeMarketingRankingList(rankingBlock);
  const hasBlockProducts =
    baseBlock.products.length > 0 ||
    Boolean(baseBlock.product_group_id || baseBlock.product_template);
  const sourceList = rawRankingList.length
    ? rawRankingList
    : hasBlockProducts
      ? [rankingBlock || {}]
      : [];
  const rankings = sourceList.map((item, index) => {
    const normalizedItem = normalizeMarketingProductBlock(item, {
      defaultTitle:
        normalizeOptionalString(item.tab_name) ||
        normalizeOptionalString(item.tab_title) ||
        normalizeOptionalString(item.ranking_title) ||
        normalizeOptionalString(item.rank_title) ||
        normalizeOptionalString(item.name) ||
        baseBlock.title,
      defaultSubTitle: '',
      defaultSort: Number(item.sort || index),
      defaultDisplayCount: baseBlock.display_count,
    });
    const tabTitle =
      normalizeOptionalString(item.tab_name) ||
      normalizeOptionalString(item.tab_title) ||
      normalizeOptionalString(item.ranking_title) ||
      normalizeOptionalString(item.rank_title) ||
      normalizeOptionalString(item.name) ||
      normalizedItem?.title ||
      '';

    return {
      ...(normalizedItem || baseBlock),
      item_index: Number.isFinite(Number(item.item_index)) ? Number(item.item_index) : index,
      title: normalizedItem?.title || tabTitle,
      tab_title: tabTitle,
      uid: normalizeOptionalString(item.uid) || undefined,
      show_rating: item.show_rating,
    };
  });
  const wapBackgroundColor =
    normalizeOptionalString(rankingBlock?.wap_background_color) ||
    normalizeOptionalString(rankingBlock?.background_color) ||
    normalizeOptionalString(rankingBlock?.bg_color);

  return {
    ...baseBlock,
    ...(wapBackgroundColor ? { wap_background_color: wapBackgroundColor } : {}),
    rankings,
  };
}

function normalizeMarketingPageStyle(pageStyle?: RawMarketingPageStyle): MarketingMainPageStyle {
  if (!pageStyle) {
    return {
      pc_background_color: LEGACY_MARKETING_MAIN_BACKGROUND_COLOR,
      wap_background_color: LEGACY_MARKETING_MAIN_BACKGROUND_COLOR,
    };
  }

  const fallbackColor =
    normalizeOptionalString(pageStyle.background_color) ||
    DEFAULT_MARKETING_MAIN_CONFIG_BACKGROUND_COLOR;
  const pcBackgroundColor =
    normalizeOptionalString(pageStyle.pc_background_color) ||
    normalizeOptionalString(pageStyle.background_color_pc) ||
    normalizeOptionalString(pageStyle.pc_bg_color) ||
    fallbackColor;
  const wapBackgroundColor =
    normalizeOptionalString(pageStyle.wap_background_color) ||
    normalizeOptionalString(pageStyle.background_color_wap) ||
    normalizeOptionalString(pageStyle.wap_bg_color) ||
    fallbackColor;

  return {
    pc_background_color: pcBackgroundColor,
    wap_background_color: wapBackgroundColor,
  };
}

function normalizeMarketingBannerType(type?: number | string): number | undefined {
  const parsed = Number(type);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeMarketingBannerList(
  bannerList: RawMarketingBannerItem[] | undefined,
  isWap: boolean,
): BannerData[] {
  if (!Array.isArray(bannerList)) {
    return [];
  }

  return bannerList
    .slice(0, 3)
    .map((item): BannerData | null => {
      const pcImage =
        normalizeOptionalString(item.pc_banner_image) ||
        normalizeOptionalString(item.pc_image) ||
        normalizeOptionalString(item.image);
      const wapImage =
        normalizeOptionalString(item.wap_banner_image) ||
        normalizeOptionalString(item.wap_image) ||
        normalizeOptionalString(item.image);
      const image = isWap ? wapImage || pcImage : pcImage || wapImage;

      if (!image) {
        return null;
      }

      const aspectRatio =
        normalizeOptionalString(item.aspectRatio) || normalizeOptionalString(item.aspect_ratio);
      const jumpContent = item.jump_content || item.jump;
      const type = normalizeMarketingBannerType(item.type);

      return {
        image,
        image_alt: normalizeOptionalString(item.image_alt) || normalizeOptionalString(item.alt),
        ...(type ? { type } : {}),
        ...(aspectRatio ? { aspectRatio } : {}),
        ...(jumpContent ? { jump_content: jumpContent } : {}),
      };
    })
    .filter((item): item is BannerData => Boolean(item));
}

function normalizeMarketingNavProductItem(
  item: RawMarketingNavProductItem | RawMarketingNavProductSection,
): MarketingNavProductItem {
  const productTemplate = normalizeMarketingProductTemplate(item.product_template);

  return {
    alt: item.alt,
    nav_title: item.nav_title,
    nav_pc_image: item.nav_pc_image,
    nav_wap_image: item.nav_wap_image,
    product_group_id: item.product_group_id,
    product_pc_image: item.product_pc_image,
    product_wap_image: item.product_wap_image,
    relation_type: item.relation_type,
    rule_type: item.rule_type,
    sort: item.sort,
    product_template: productTemplate,
  };
}

function normalizeMarketingNavProductList(
  blocks: RawBlocks,
  marketingData: RawMarketingData,
): MarketingNavProductItem[] {
  if (marketingData.nav_product?.enabled === false) {
    return [];
  }

  if (Array.isArray(blocks.nav_product_sections)) {
    return blocks.nav_product_sections
      .filter((item) => {
        const productTemplate = item.product_template;
        return (
          productTemplate?.visible === true &&
          Array.isArray(productTemplate.products) &&
          productTemplate.products.length > 0
        );
      })
      .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
      .map(normalizeMarketingNavProductItem);
  }

  return Array.isArray(blocks.marketing_data?.nav_product_list)
    ? blocks.marketing_data.nav_product_list.map(normalizeMarketingNavProductItem)
    : Array.isArray(marketingData.nav_product_list)
      ? marketingData.nav_product_list.map(normalizeMarketingNavProductItem)
      : Array.isArray(marketingData.nav_product?.nav_product_list)
        ? marketingData.nav_product.nav_product_list.map(normalizeMarketingNavProductItem)
        : Array.isArray(marketingData.nav_product?.my_product_list)
          ? marketingData.nav_product.my_product_list.map(normalizeMarketingNavProductItem)
          : [];
}

function normalizeMarketingMainModuleConfigs({
  hasBanner,
  couponConfig,
  couponBlock,
  flashSaleBlock,
  brandProductsBlock,
  sceneProductsBlock,
  rankingBlock,
  navProductList,
}: {
  hasBanner: boolean;
  couponConfig?: MarketingCouponConfig;
  couponBlock?: MarketingCouponBlockConfig;
  flashSaleBlock?: MarketingFlashSaleBlockConfig;
  brandProductsBlock?: MarketingProductBlockConfig;
  sceneProductsBlock?: MarketingSceneProductsBlockConfig;
  rankingBlock?: MarketingRankingBlockConfig;
  navProductList: MarketingNavProductItem[];
}): MarketingMainModuleConfig[] {
  const modules: MarketingMainModuleConfig[] = [];

  if (hasBanner) {
    modules.push({
      id: 'marketing-main-banner',
      type: MarketingMainModuleType.BANNER,
      sort: 10,
    });
  }

  if (
    couponBlock ? couponBlock.visible : couponConfig?.visible && couponConfig.coupons.length > 0
  ) {
    modules.push({
      id: 'marketing-main-coupon',
      type: MarketingMainModuleType.COUPON,
      sort: couponBlock?.sort ?? 20,
    });
  }

  if (flashSaleBlock?.visible) {
    modules.push({
      id: 'marketing-main-flash-sale',
      type: MarketingMainModuleType.FLASH_SALE,
      sort: flashSaleBlock.sort,
    });
  }

  if (brandProductsBlock?.visible) {
    modules.push({
      id: 'marketing-main-brand-products',
      type: MarketingMainModuleType.BRAND_PRODUCTS,
      sort: brandProductsBlock.sort,
    });
  }

  if (sceneProductsBlock?.visible) {
    modules.push({
      id: 'marketing-main-scene-products',
      type: MarketingMainModuleType.SCENE_PRODUCTS,
      sort: sceneProductsBlock.sort,
    });
  }

  if (rankingBlock?.visible) {
    modules.push({
      id: 'marketing-main-ranking',
      type: MarketingMainModuleType.RANKING,
      sort: rankingBlock.sort,
    });
  }

  if (navProductList.length > 0) {
    modules.push({
      id: 'marketing-main-navigation-products',
      type: MarketingMainModuleType.NAVIGATION_PRODUCTS,
      sort: 26,
    });
  }

  return modules.sort((a, b) => a.sort - b.sort);
}

/** 种草坑位素材（接口原始字段名 material，单数） */
export interface SeedingMaterial {
  id: number;
  media_type?: string; // 'image' | 'video'
  name?: string;
  path?: string;
  is_expired?: boolean;
}

/** 种草坑位关联商品 */
export interface SeedingProduct {
  shop_sku: string;
  product_name: string;
  main_image?: string;
  price?: string;
  original_price?: string;
}

/** 种草评论（列表坑位 comment、详情 featured_comments 项） */
export interface SeedingFeaturedComment {
  reviewer?: string;
  content?: string;
}

/** 种草坑位 */
export interface SeedingItem {
  id?: number;
  item_id?: number;
  kol_name?: string;
  /** 列表坑位评论 */
  comment?: SeedingFeaturedComment;
  product_id?: number;
  product_name?: string;
  material?: SeedingMaterial; // 接口返回单数 material，非数组
  product?: SeedingProduct;
  cover_image?: string;
  cover_width?: number;
  cover_height?: number;
  is_expired?: boolean;
}

/** 种草坑位列表响应 */
export interface SeedingItemsResponse {
  items: SeedingItem[];
  total: number;
  has_more: boolean;
  page?: number;
  page_size?: number;
}

export interface MarketingProductListResponse {
  type?:
    | 'product_group'
    | 'flash_sale'
    | 'brand_product'
    | 'scene_product'
    | 'ranking_product'
    | string;
  ref_id?: number;
  customer_segment?: PromotionFlashSaleCustomerSegment;
  group?: GuideProductGroupInfo;
  items?: any[];
  list?: any[];
  page?: number;
  page_size?: number;
  total?: number;
  all_total?: number;
  has_more?: boolean;
  image_rule?: unknown[];
  hover_image_rule?: unknown[];
  countdown_end_at?: string | null;
  remaining_seconds?: number | null;
}

export interface MarketingBrandProductListResponse extends MarketingProductListResponse {
  type?: 'brand_product';
}

export interface MarketingSceneProductListResponse extends MarketingProductListResponse {
  type?: 'scene_product';
}

export interface MarketingRankingProductListResponse extends MarketingProductListResponse {
  type?: 'ranking_product';
}

export interface MarketingFlashSaleProductListResponse
  extends Omit<MarketingProductListResponse, 'customer_segment'> {
  type: 'flash_sale';
  status: MarketingFlashSaleStatus;
  visible_state: MarketingFlashSaleVisibleState;
  flash_sale_id: number;
  customer_segment: MarketingFlashSaleCustomerSegment;
  reach_limit_rule: MarketingFlashSaleReachLimitRule;
  participated: boolean;
  last_paid_date: string | null;
  last_paid_participation_date: string | null;
  is_reached_limit: boolean;
  is_targeted: boolean;
  countdown_end_at: string | null;
  remaining_seconds: number;
}

/** 种草详情素材 */
export interface SeedingDetailMaterial {
  id: number;
  marketing_page_seeding_item_id?: number;
  media_type?: string;
  name?: string;
  path?: string;
  expires_at?: string;
  sort?: number;
  payload?: unknown[];
  is_expired?: boolean;
}

/** 种草详情 payload */
export interface SeedingDetailPayload {
  kol_name?: string;
  featured_comments?: SeedingFeaturedComment[];
}

/** 种草详情 seeding 节点 */
export interface SeedingDetailInfo {
  item_id: number;
  sort?: number;
  payload?: SeedingDetailPayload;
  materials?: SeedingDetailMaterial[];
}

/** 种草详情商品图片 */
export interface SeedingDetailProductImage {
  product?: string[];
  video?: string[];
  material?: string[];
  celebrity?: string[];
  product_detail?: string[];
  frame_background_image?: string[];
  background_image_of_mirror_leg?: string[];
}

/** 种草详情商品 */
export interface SeedingDetailProduct {
  id: number;
  product_frame_color_name?: string;
  shop_sku: string;
  sku?: string;
  business_sku?: string;
  name: string;
  short_name?: string;
  status?: number;
  image?: SeedingDetailProductImage;
  price?: string;
  price_info?: Array<{
    currency: string;
    price: string;
    sell_price: string;
  }>;
  is_wish?: number;
  wish_num?: number;
  products_category?: string;
}

/** 种草坑位详情响应 */
export interface SeedingItemDetailResponse {
  product?: SeedingDetailProduct;
  seeding?: SeedingDetailInfo;
}

export interface MarketingMainNavigationItem {
  title: string;
  image: string;
  wap_image?: string;
  alt?: string;
}

export interface MarketingMainCommodityVersion {
  product_group_id: number;
  image?: string;
  wap_image?: string;
  image_alt?: string;
  title: number;
  default_num: number;
  product_list: any[];
  image_title?: string;
  has_more?: boolean;
  relation_type?: string;
  rule_type?: string;
}

export interface MarketingMainPageData {
  page_name?: string;
  banner?: BannerData | null;
  wap_banner?: BannerData | null;
  banner_list: BannerData[];
  navigate: MarketingMainNavigationItem[];
  commodity_version: MarketingMainCommodityVersion[];
  faq?: FaqData | null;
}

export interface MarketingMainBannerModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.BANNER;
  banner: BannerData | null;
  banners: BannerData[];
}

export interface MarketingMainCouponModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.COUPON;
  couponConfig?: MarketingCouponConfig;
  couponBlock?: MarketingCouponBlockConfig;
}

export interface MarketingMainFlashSaleModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.FLASH_SALE;
  flashSaleBlock: MarketingFlashSaleBlockConfig;
}

export interface MarketingMainBrandProductsModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.BRAND_PRODUCTS;
  brandProductsBlock: MarketingProductBlockConfig;
}

export interface MarketingMainSceneProductsModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.SCENE_PRODUCTS;
  sceneProductsBlock: MarketingSceneProductsBlockConfig;
}

export interface MarketingMainRankingModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.RANKING;
  rankingBlock: MarketingRankingBlockConfig;
}

export interface MarketingMainNavigationProductsModule extends MarketingMainModuleConfig {
  type: MarketingMainModuleType.NAVIGATION_PRODUCTS;
  navigationData: MarketingMainNavigationItem[];
  commodityVersionList: MarketingMainCommodityVersion[];
}

export type MarketingMainResolvedModule =
  | MarketingMainBannerModule
  | MarketingMainCouponModule
  | MarketingMainFlashSaleModule
  | MarketingMainBrandProductsModule
  | MarketingMainSceneProductsModule
  | MarketingMainRankingModule
  | MarketingMainNavigationProductsModule;

export interface MarketingMainServerData {
  pageData: MarketingMainPageData | null;
  commodityVersionList: MarketingMainCommodityVersion[];
  navigationData: MarketingMainNavigationItem[];
  bannerData: BannerData | null;
  bannerList: BannerData[];
  couponConfig?: MarketingCouponConfig;
  modules: MarketingMainResolvedModule[];
}

export interface GuideSecondaryNavConfig {
  label: string;
  isDefault?: boolean;
  hidden?: boolean;
  productPoolTitle?: string;
  productPoolSubtitle?: string;
  groupId?: number;
  navImage?: string;
  bannerImage?: string;
  labelBg?: string;
  relatedBlockLinks?: Array<{ link_name: string; path?: string; group_link?: string }>;
  titleColor?: string;
  h1?: string;
  faq?: FaqItem[];
}

export interface GuidePrimaryNavConfig {
  label: string;
  hasImage?: boolean;
  bannerImage?: string;
  secondaryNav: GuideSecondaryNavConfig[];
}

export interface GuidePageConfig {
  navigation: {
    wap: GuidePrimaryNavConfig[];
    web: GuidePrimaryNavConfig[];
  };
}

export type GuideProductGroupInfo = object & {
  banner_down_info?: {
    title?: string;
    desc?: string;
    related_link_list?: Array<{ link_name?: string; path?: string; group_link?: string }>;
  };
};

export interface GuideServerData {
  group?: GuideProductGroupInfo;
  products: any[];
  total: number;
  allTotal: number;
  imageRule?: any[];
  hoverImageRule?: any[];
  currentGroupId: number;
  pageConfig: GuidePageConfig;
}

export interface PromotionPageData {
  banner?: BannerData | null;
  flash_sale?: {
    config?: PromotionCustomerFlashSale;
    new_customer_config?: PromotionCustomerFlashSale;
    old_customer_config?: PromotionCustomerFlashSale;
    new_user_ref_id?: number;
    old_user_ref_id?: number;
    products: IProductListItem[];
    customer_segment?: PromotionFlashSaleCustomerSegment;
    countdown_end_at?: string | null;
    remaining_seconds?: number | null;
    sold_out_sort?: string | number;
    sold_out_display?: string | number;
  };
  promotion?: {
    title_image?: string;
    title_image_alt?: string;
    cart_image?: string;
    group_id?: string;
  };
  bottom_banner: Array<{
    id: number;
    image: string;
    image_alt?: string;
    jump_content?: JumpTarget;
  }>;
}

export interface PromotionServerData {
  pageData: PromotionPageData;
  promotionList: IProductListItem[];
  promotionLimit: number;
  promotionHasMore: boolean;
}

/** 页面服务端数据 */
export interface CampaignsServerData {
  runtimeConfig: MarketingRuntimeConfig;
  activityType: MarketingPageTemplateType;
  isPreview: boolean;
  isBot?: boolean;
  adp?: string;
  deviceType: number;
  bannerData: BannerData | null;
  seoData: SeoData | null;
  faqData: FaqData | null;
  seedingItems: SeedingItemsResponse | null;
  marketingMainData: MarketingMainServerData | null;
  guideData: GuideServerData | null;
  promotionData: PromotionServerData | null;
  styleHotProductsData: StyleHotProductRuntimeData | null;
}

/** 客户端组件 Props */
export interface CampaignsClientProps {
  serverData: CampaignsServerData;
}

// ==================== 数据规范化映射 ====================

/**
 * 将接口原始数据映射为业务使用的规范化结构
 * 接口返回的 data 字段包含 page_meta / seo / blocks 嵌套，
 * 需要展平为 MarketingRuntimeConfig 的扁平结构
 */
export function normalizeMarketingConfig(
  raw: RawMarketingRuntimeConfigData,
  deviceType?: EPlatform,
): MarketingRuntimeConfig {
  const pm = raw.page_meta || {};
  const seo = raw.seo || {};
  const blocks = raw.blocks || {};
  const rawBanner = blocks.ad_banner;
  const rawMarketingData: RawMarketingData = {
    schema_version: blocks.schema_version,
    page_style: blocks.page_style,
    banner_list: blocks.banner_list,
    coupon_block: blocks.coupon_block,
    flash_sale_block: blocks.flash_sale_block,
    brand_products: blocks.brand_products,
    scene_products: blocks.scene_products,
    ranking_block: blocks.ranking_block,
    nav_product: blocks.nav_product,
    ...(blocks.marketing_data || {}),
  };
  const isWap = !!deviceType && deviceType !== EPlatform.WEB;
  const faqData = normalizeRuntimeFaqData(raw, blocks, seo);
  const hasSeoConfig =
    seo.seo_title ||
    seo.seo_description ||
    seo.seo_keywords ||
    seo.seo_og_image ||
    seo.h_one ||
    seo.seo_h1 ||
    seo.geo_language_mark;
  const couponConfig = normalizeMarketingCouponConfig(
    blocks.coupon_config || rawMarketingData.coupon_block,
  );
  const couponBlock = normalizeMarketingCouponBlock(rawMarketingData.coupon_block);
  const flashSaleBlock = normalizeMarketingFlashSaleBlock(rawMarketingData.flash_sale_block);
  const brandProductsBlock = normalizeMarketingProductBlock(rawMarketingData.brand_products, {
    defaultTitle: '',
    defaultSubTitle: '',
    defaultSort: 27,
    defaultDisplayCount: 8,
  });
  const sceneProductsBlock = normalizeMarketingSceneProductsBlock(rawMarketingData.scene_products);
  const rankingBlock = normalizeMarketingRankingBlock(rawMarketingData.ranking_block);
  const navProductList = normalizeMarketingNavProductList(blocks, rawMarketingData);
  const pageStyle = normalizeMarketingPageStyle(rawMarketingData.page_style);
  const adBanner =
    rawBanner?.pc_banner_image || rawBanner?.wap_banner_image
      ? {
          image: isWap
            ? (rawBanner.wap_banner_image ?? rawBanner.pc_banner_image!)
            : (rawBanner.pc_banner_image ?? rawBanner.wap_banner_image!),
          image_alt: rawBanner.alt,
          jump_content: rawBanner.jump,
        }
      : undefined;
  const wapAdBanner = rawBanner?.wap_banner_image
    ? {
        image: rawBanner.wap_banner_image,
        image_alt: rawBanner.alt,
        jump_content: rawBanner.jump,
      }
    : undefined;
  const configuredBannerList = normalizeMarketingBannerList(rawMarketingData.banner_list, isWap);
  const marketingBannerList = configuredBannerList.length
    ? configuredBannerList
    : adBanner
      ? [adBanner]
      : [];
  const marketingModules = normalizeMarketingMainModuleConfigs({
    hasBanner: marketingBannerList.length > 0,
    couponConfig,
    couponBlock,
    flashSaleBlock,
    brandProductsBlock,
    sceneProductsBlock,
    rankingBlock,
    navProductList,
  });

  return {
    ...raw,
    // page_meta 展平
    validity_type: pm.validity_type,
    end_at: pm.end_at,
    end_method: pm.end_method,
    end_jump: pm.end_jump,
    // 原始字段透传
    template_type: raw.template_type,
    page_title: raw.page_title,
    page_name: raw.page_name || raw.page_title,
    page_status: raw.page_status,
    activity_id: raw.activity_id,
    activity_name: raw.activity_name,
    marketing_page_id: raw.marketing_page_id,
    // SEO 字段名映射
    seo: hasSeoConfig
      ? {
          title: seo.seo_title,
          description: seo.seo_description,
          keywords: seo.seo_keywords,
          image: seo.seo_og_image,
          hOne: seo.h_one,
          seoH1: seo.seo_h1,
          geoLanguageMark: seo.geo_language_mark,
        }
      : undefined,
    // Banner 映射：从 blocks.ad_banner 提取，按端选择图片
    ad_banner: adBanner,
    wap_ad_banner: wapAdBanner,
    // FAQ 兼容原活动页 faq/faq_part 结构，并保留 seo.faq_list 兜底
    faq: faqData,
    // 商品推荐版位配置从 blocks.product_recommend 提取
    product_recommend: blocks.product_recommend
      ? {
          group_id: blocks.product_recommend.group_id,
          source_type:
            (blocks.product_recommend.source_type as ProductRecommendConfig['source_type']) || '',
          section_title: blocks.product_recommend.section_title,
          algorithm_value: blocks.product_recommend.algorithm_value,
        }
      : undefined,
    coupon_config: couponConfig,
    guide_data: blocks.guide_data
      ? {
          pc_nav_list: Array.isArray(blocks.guide_data.pc_nav_list)
            ? blocks.guide_data.pc_nav_list.map((item) => ({
                image: item.image,
                title: item.title,
                children: Array.isArray(item.children)
                  ? item.children.map((child) => ({
                      image: child.image,
                      title: child.title,
                      is_show: child.is_show,
                      banner_image: child.banner_image,
                      product_group_id: child.product_group_id,
                    }))
                  : [],
              }))
            : [],
          wap_nav_list: Array.isArray(blocks.guide_data.wap_nav_list)
            ? blocks.guide_data.wap_nav_list.map((item) => ({
                image: item.image,
                title: item.title,
                children: Array.isArray(item.children)
                  ? item.children.map((child) => ({
                      image: child.image,
                      title: child.title,
                      is_show: child.is_show,
                      banner_image: child.banner_image,
                      product_group_id: child.product_group_id,
                    }))
                  : [],
              }))
            : [],
        }
      : undefined,
    marketing_data: {
      schema_version: rawMarketingData.schema_version,
      page_style: pageStyle,
      modules: marketingModules,
      banner_list: marketingBannerList,
      coupon_block: couponBlock,
      flash_sale_block: flashSaleBlock,
      brand_products: brandProductsBlock,
      scene_products: sceneProductsBlock,
      ranking_block: rankingBlock,
      nav_product_list: navProductList,
    },
    promotion_data: blocks.promotion_data
      ? {
          fixed_ads: Array.isArray(blocks.promotion_data.fixed_ads)
            ? blocks.promotion_data.fixed_ads.map((item) => ({
                alt: item.alt,
                jump: item.jump,
                pc_image: item.pc_image,
                wap_image: item.wap_image,
              }))
            : [],
          flash_sale: blocks.promotion_data.flash_sale
            ? {
                new_customer: blocks.promotion_data.flash_sale.new_customer,
                old_customer: blocks.promotion_data.flash_sale.old_customer,
                sold_out_sort: blocks.promotion_data.flash_sale.sold_out_sort,
                sold_out_display: blocks.promotion_data.flash_sale.sold_out_display,
              }
            : undefined,
          promotion_position: blocks.promotion_data.promotion_position
            ? {
                alt: blocks.promotion_data.promotion_position.alt,
                title_pc_image: blocks.promotion_data.promotion_position.title_pc_image,
                title_wap_image: blocks.promotion_data.promotion_position.title_wap_image,
                product_group_id: blocks.promotion_data.promotion_position.product_group_id,
              }
            : undefined,
        }
      : undefined,
  } as MarketingRuntimeConfig;
}
